import React, { useEffect, useRef, useState } from 'react';
import { Coordinates } from '../Types/DraftOperation';
import LatLng = google.maps.LatLng;

type EnhancedCoordinates = Coordinates & {
  sequence?: number;
  isFactory?: boolean;
  waitingTime?: number;
  title?: string;
};

type MapProps = {
  markerPositions: EnhancedCoordinates[];
  showSequenceNumbers?: boolean;
  showWaitingTime?: boolean;
  onTimelineCalculated?: (totalMinutes: number) => void;
  onMarkerClick?: (index: number) => void;
  focusedIndex?: number | null;
};

const defaultProps = {
  showSequenceNumbers: false,
  showWaitingTime: false,
  onTimelineCalculated: undefined,
  onMarkerClick: undefined,
  focusedIndex: null,
};

const RZMap = ({
  markerPositions,
  showSequenceNumbers = false,
  showWaitingTime = false,
  onTimelineCalculated,
  onMarkerClick,
  focusedIndex,
}: MapProps): JSX.Element => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [totalTime, setTotalTime] = useState<number>(0);

  useEffect(() => {
    (async () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const imports = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      const { PinElement, AdvancedMarkerElement } = imports;

      if (mapRef.current) {
        const bound = new google.maps.LatLngBounds();

        const map = new window.google.maps.Map(mapRef.current!, {
          center: { lat: -34.397, lng: 150.644 },
          zoom: 8,
          mapId: 'DEMO_MAP',
        });
        mapInstanceRef.current = map;
        const processedMarkers = [...markerPositions]
          .filter((a) => !!a)
          .map((marker, index) => ({
            ...marker,
            sequence: marker.sequence || index + 1,
            waitingTime: marker.waitingTime || 0,
          }));

        if (showSequenceNumbers) {
          processedMarkers.sort((a, b) => a.sequence! - b.sequence!);
        }

        const totalWaitingTime = processedMarkers.reduce((acc, marker) => acc + (marker.waitingTime || 0), 0);
        setTotalTime(totalWaitingTime);

        if (onTimelineCalculated) {
          onTimelineCalculated(totalWaitingTime);
        }

        processedMarkers.forEach(
          ({ lat, lng, draggable, onDragEnd, mapColor, sequence, isFactory, waitingTime, title }, markerIndex) => {
            if (showSequenceNumbers || showWaitingTime) {
              const markerContainer = document.createElement('div');
              markerContainer.style.position = 'relative';

              const pinColor = mapColor || (isFactory ? '#FF0000' : '#4285F4');
              const pinBackground = new PinElement({
                background: pinColor,
                borderColor: isFactory ? '#000000' : undefined,
                scale: isFactory ? 1.2 : 1,
              });

              markerContainer.appendChild(pinBackground.element);

              const label = document.createElement('div');
              label.style.position = 'absolute';
              label.style.top = '50%';
              label.style.left = '50%';
              label.style.transform = 'translate(-50%, -50%)';
              label.style.color = '#FFFFFF';
              label.style.fontWeight = 'bold';
              label.style.fontSize = '12px';

              if (showSequenceNumbers) {
                label.textContent = sequence!.toString();
              }

              markerContainer.appendChild(label);

              let infoContent = isFactory ? 'Factory' : `Location ${sequence}`;

              if (showWaitingTime && waitingTime) {
                infoContent += ` (Wait: ${waitingTime} min)`;
              }

              const marker = new AdvancedMarkerElement({
                position: { lat, lng },
                map,
                draggable,
                content: markerContainer,
                title: title || infoContent,
              });

              if (showWaitingTime && waitingTime) {
                const infoWindow = new google.maps.InfoWindow({
                  content: `<div style="padding: 8px;">
                  <strong>${isFactory ? 'Factory' : `Location ${sequence}`}</strong>
                  <p>Waiting time: ${waitingTime} minutes</p>
                </div>`,
                });

                marker.addListener('click', () => {
                  infoWindow.open(map, marker);
                });
              }

              if (onDragEnd) {
                marker.addListener('dragend', onDragEnd);
              }
              if (onMarkerClick) {
                marker.addListener('click', () => onMarkerClick(markerIndex));
              }
            } else {
              const pinBackground = new PinElement({
                background: mapColor || '#4285F4',
              });

              const marker = new AdvancedMarkerElement({
                position: { lat, lng },
                map,
                draggable,
                content: pinBackground.element,
                title: title || undefined,
              });

              if (onDragEnd) {
                marker.addListener('dragend', onDragEnd);
              }
              if (onMarkerClick) {
                marker.addListener('click', () => onMarkerClick(markerIndex));
              }
            }

            bound.extend(new LatLng({ lat, lng }));
          },
        );

        map.fitBounds(bound);
        if (processedMarkers.length === 1) {
          map.setZoom(showSequenceNumbers ? 10 : 1);
        }
      }
    })();
  }, [markerPositions, showSequenceNumbers, showWaitingTime, onTimelineCalculated]);

  useEffect(() => {
    if (focusedIndex === null || focusedIndex === undefined) return;
    const map = mapInstanceRef.current;
    const marker = markerPositions[focusedIndex];
    if (map && marker) {
      map.panTo({ lat: marker.lat, lng: marker.lng });
      map.setZoom(15);
    }
  }, [focusedIndex, markerPositions]);

  return (
    <div className="map-component">
      <div style={{ height: `400px` }} ref={mapRef} />
      {showWaitingTime && (
        <div
          className="timeline-info"
          style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}
        >
          <strong>Total Waiting Time:</strong> {totalTime} minutes
        </div>
      )}
    </div>
  );
};

// Assign default props
RZMap.defaultProps = defaultProps;

export default RZMap;
